import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  countGuestCrawls,
  listCrawls,
  listProjects,
  type CrawlSummary,
  type Entitlements,
  type ProjectSummary,
} from "../lib/api";
import { planBadgeClass, planLabel, useAuth } from "../lib/auth";
import { ProjectMembersDialog } from "../components/ProjectMembersDialog";
import { DeleteProjectDialog } from "../components/DeleteProjectDialog";

/** Max cards rendered on the Dashboard grid — anything beyond this lives on /runs. */
const DASHBOARD_CARD_LIMIT = 12;

/**
 * Dashboard landing. Renders one card per crawl (capped at 12 for a
 * quick-glance "workspace status" view). The full history with filters
 * and pagination lives on /runs. Card shape + color language lifted from
 * UAIP-Mockups.html §Dashboard (slate-900 panels, emerald pulse for recent).
 *
 * Visibility follows the API's session gate:
 *   - signed-in user → only their own crawls
 *   - anonymous visitor → the public "workspace" (dev-seed tenancy + orphan
 *     folders); other users' private crawls are hidden server-side.
 */
export function DashboardPage(): JSX.Element {
  const [crawls, setCrawls] = useState<CrawlSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** When the authed user has 0 owned crawls, we probe the guest workspace
   *  count separately so the empty state can offer a "View as guest" link
   *  with concrete numbers. `null` = not yet checked / not relevant. */
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const { user, entitlements } = useAuth();
  const [searchParams] = useSearchParams();
  // ?guest=1 → render the guest workspace even when the user is signed in.
  // Lets people see crawls created before they signed in without logging out.
  const guestMode = searchParams.get("guest") === "1";

  useEffect(() => {
    let cancelled = false;
    setCrawls(null);
    setError(null);
    setGuestCount(null);

    const fetchOptions = guestMode
      ? { limit: DASHBOARD_CARD_LIMIT, scope: "guest" as const }
      : { limit: DASHBOARD_CARD_LIMIT };

    listCrawls(fetchOptions)
      .then(async (list) => {
        if (cancelled) return;
        setCrawls(list);
        // Empty list + signed-in + NOT already in guest mode → probe the
        // guest workspace count so the empty state can offer the toggle.
        if (list.length === 0 && user && !guestMode) {
          try {
            const n = await countGuestCrawls();
            if (!cancelled) setGuestCount(n);
          } catch {
            // Best-effort: if the probe fails, the empty state still
            // renders — just without the guest count.
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [user, guestMode]);

  return (
    <div>
      {user && !guestMode && <ProjectsQuotaCard entitlements={entitlements} />}

      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Crawls</h1>
          <p className="text-sm text-slate-400 mt-1">
            {/*
              Heading copy is data-aware so we don't lie when the list is
              empty. Three branches:
              - guest mode (any auth): "Guest workspace — N visible (12 cap)"
              - authed + own list non-empty: "Your N most recent crawls"
              - authed + own list empty: drop the "Your N" promise — let
                the EmptyState below carry the explanation, just leave a
                short scoping note here so the header isn't bare.
              - anonymous: original public-workspace copy.
            */}
            {(() => {
              const count = crawls?.length ?? 0;
              if (guestMode) {
                return `Guest workspace — showing ${count} of the ${DASHBOARD_CARD_LIMIT} most recent. `;
              }
              if (user) {
                return count > 0
                  ? `Your ${count} most recent crawl${count === 1 ? "" : "s"} (capped at ${DASHBOARD_CARD_LIMIT}). `
                  : "Showing crawls owned by your account. ";
              }
              return `The public workspace — ${DASHBOARD_CARD_LIMIT} most recent crawls. `;
            })()}
            <Link to="/runs" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
              View full history →
            </Link>
            {guestMode && (
              <>
                {" · "}
                <Link
                  to="/"
                  className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2"
                >
                  Back to your workspace
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">
          api: /api/crawls{guestMode ? "?scope=guest" : ""}
        </div>
      </header>

      {error && <ErrorBanner message={error} />}
      {crawls === null && !error && <LoadingBanner />}
      {crawls !== null && crawls.length === 0 && (
        <EmptyState
          showGuestCta={user !== null && !guestMode && (guestCount ?? 0) > 0}
          guestCount={guestCount ?? 0}
        />
      )}

      {crawls && crawls.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {crawls.map((c) => (
            <CrawlCard key={c.id} crawl={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CrawlCard({ crawl }: { crawl: CrawlSummary }): JSX.Element {
  const isRecent = isWithinLast24h(crawl.crawledAt);
  return (
    <Link
      to={`/crawls/${encodeURIComponent(crawl.id)}`}
      className="group block rounded-xl border border-slate-800 bg-slate-900/60 p-5 hover:border-slate-600 hover:bg-slate-900 transition"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-slate-200 truncate">
              {crawl.id}
            </span>
            {isRecent && (
              <span
                className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot"
                title="crawled in the last 24h"
              />
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1 truncate">
            {crawl.targetUrl}
          </div>
        </div>
        <FrameworkBadge framework={crawl.framework} authed={crawl.authed} />
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <Stat label="pages" value={String(crawl.pageCount)} />
        <Stat label="elements" value={String(crawl.elementCount)} />
        <Stat
          label="duration"
          value={formatDuration(crawl.durationMs)}
        />
      </dl>

      <div className="mt-4 text-xs text-slate-500 flex items-center justify-between">
        <span>{formatDate(crawl.crawledAt)}</span>
        <span className="text-indigo-400 group-hover:text-indigo-300">
          open →
        </span>
      </div>
    </Link>
  );
}

function FrameworkBadge({
  framework,
  authed,
}: {
  framework: CrawlSummary["framework"];
  authed: boolean;
}): JSX.Element {
  const tone =
    framework === "java"
      ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
      : framework === "python"
      ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
      : "bg-slate-700/30 text-slate-400 border-slate-600/40";
  return (
    <div className="flex items-center gap-1">
      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded border ${tone}`}>
        {framework}
      </span>
      {authed && (
        <span
          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
          title="auth injected"
        >
          auth
        </span>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="font-mono text-sm text-slate-100">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
    </div>
  );
}

/**
 * Tier-aware projects quota widget, rendered at the top of the Dashboard
 * for signed-in users.
 *
 * Mental model: 1 "project" = 1 target web application (many crawls /
 * runs / schedules over its lifetime). The catalog caps are Free=1,
 * Starter=3, Pro=10, Enterprise=∞ (modeled as `maxProjects <= 0`).
 *
 * We read `projectCount` + `maxProjects` directly off the Entitlements
 * snapshot stamped onto /api/auth/me — no extra round trip. When the
 * server couldn't stamp a count (pre-migration API, transient DB error)
 * we fall back to rendering just the cap with no progress bar.
 */
function ProjectsQuotaCard({
  entitlements,
}: {
  entitlements: Entitlements;
}): JSX.Element | null {
  const plan = entitlements.plan;
  const cap = entitlements.maxProjects ?? 0;
  const count = entitlements.projectCount;
  const unlimited = cap <= 0;
  const canUpgrade = plan !== "pro" && plan !== "enterprise" && plan !== "admin";

  // Inline expandable list — lets the user actually see WHICH projects
  // exist (auto-creation by hostname is invisible until you can name them).
  // Click "Show projects" → fetches /api/projects, renders rows with a
  // delete button. Refetches after a delete so the list stays fresh.
  const [expanded, setExpanded] = useState(false);
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [membersFor, setMembersFor] = useState<ProjectSummary | null>(null);
  const [deleteFor, setDeleteFor] = useState<ProjectSummary | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  async function loadProjects(): Promise<void> {
    setListError(null);
    try {
      const list = await listProjects();
      setProjects(list);
    } catch (err) {
      setListError(err instanceof Error ? err.message : String(err));
    }
  }

  function toggleExpanded(): void {
    const next = !expanded;
    setExpanded(next);
    if (next && projects === null) void loadProjects();
  }

  // Delete is gated behind the DeleteProjectDialog — type-to-confirm +
  // impact summary (crawl count, schedule warning). The dialog does the
  // actual fetch; this row-level handler just opens it. The simpler
  // window.confirm approach was replaced because (a) it doesn't show
  // the user that schedules will silently respawn the project, and
  // (b) one click on a misclick was destroying real data.
  function handleDelete(p: ProjectSummary): void {
    setDeleteFor(p);
  }

  // Anonymous: no projects at all. AuthProvider would have returned
  // user=null, so we wouldn't even be rendered — but guard just in case.
  if (plan === "anonymous") return null;

  const usedLabel =
    typeof count === "number"
      ? unlimited
        ? `${count} projects`
        : `${count} / ${cap} projects`
      : unlimited
      ? "Unlimited projects"
      : `Up to ${cap} ${cap === 1 ? "project" : "projects"}`;

  const pct =
    typeof count === "number" && !unlimited && cap > 0
      ? Math.min(100, Math.round((count / cap) * 100))
      : 0;
  const atCap = typeof count === "number" && !unlimited && count >= cap;
  const barTone = atCap
    ? "bg-rose-500"
    : pct >= 80
    ? "bg-amber-400"
    : "bg-indigo-500";

  return (
    <section className="mb-6 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-200">Projects</h2>
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${planBadgeClass(
                plan,
              )}`}
            >
              {planLabel(plan)}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {unlimited
              ? "Your plan includes unlimited target web applications."
              : "One project = one target web application (with many crawls over time)."}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm text-slate-100">
            {unlimited ? "∞" : `${count ?? "—"} / ${cap}`}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">
            {usedLabel}
          </div>
        </div>
      </div>

      {!unlimited && typeof count === "number" && cap > 0 && (
        <div className="mt-4 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full ${barTone} transition-[width] duration-500`}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={cap}
            aria-valuenow={count}
            aria-label="Projects used"
          />
        </div>
      )}

      {atCap && canUpgrade && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          <span>
            You&apos;ve hit your {planLabel(plan)} plan&apos;s project cap.
            Upgrade to add more.
          </span>
          <Link
            to="/account"
            className="font-medium text-amber-100 hover:text-white underline underline-offset-2"
          >
            Upgrade →
          </Link>
        </div>
      )}

      {!atCap && canUpgrade && (
        <div className="mt-3 text-[11px] text-slate-500">
          Need more projects?{" "}
          <Link
            to="/account"
            className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2"
          >
            Upgrade your plan
          </Link>
          .
        </div>
      )}

      {/* Inline list — only meaningful when count is non-zero. The toggle
       *  is always visible for parity but the list is bypassed when there's
       *  nothing to show.
       */}
      {typeof count === "number" && count > 0 && (
        <div className="mt-4 border-t border-slate-800 pt-3">
          <button
            type="button"
            onClick={toggleExpanded}
            className="text-xs text-slate-300 hover:text-slate-100 inline-flex items-center gap-1"
          >
            <span>{expanded ? "▾" : "▸"}</span>
            <span>{expanded ? "Hide projects" : `Show ${count} project${count === 1 ? "" : "s"}`}</span>
          </button>

          {expanded && (
            <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40">
              {listError && (
                <div className="px-3 py-2 text-xs text-rose-300 border-b border-slate-800">
                  {listError}
                </div>
              )}
              {projects === null && !listError && (
                <div className="px-3 py-3 text-xs text-slate-500">Loading…</div>
              )}
              {projects && projects.length === 0 && (
                <div className="px-3 py-3 text-xs text-slate-500">
                  No project rows returned (cache mismatch with the count
                  above? try refreshing the page).
                </div>
              )}
              {projects && projects.length > 0 && (
                <ul className="divide-y divide-slate-800">
                  {projects.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 text-xs"
                    >
                      <span className="font-mono text-slate-100 truncate flex-1">
                        {p.name}
                      </span>
                      <span className="text-[10px] text-slate-500 shrink-0">
                        created {fmtDate(p.createdAt)}
                      </span>
                      {/* Members management is a Team/Enterprise
                       *  feature (Plan §21 Phase 4/5). Hidden on Free,
                       *  Starter, and Pro so the upgrade path is
                       *  obvious — server still 402's defensively. */}
                      {entitlements.teamCollaboration && (
                        <button
                          type="button"
                          onClick={() => setMembersFor(p)}
                          className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800"
                        >
                          members
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(p)}
                        className="rounded border border-rose-700/50 px-2 py-0.5 text-[11px] text-rose-300 hover:bg-rose-900/30"
                      >
                        delete
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      {membersFor && (
        <ProjectMembersDialog
          projectId={membersFor.id}
          projectName={membersFor.name}
          onClose={() => setMembersFor(null)}
        />
      )}
      {deleteFor && (
        <DeleteProjectDialog
          project={deleteFor}
          onClose={() => setDeleteFor(null)}
          onDeleted={() => {
            setDeleteFor(null);
            void loadProjects();
          }}
        />
      )}
    </section>
  );
}

/** Format a project's createdAt (ms epoch) as a short date. Uses UTC so
 *  the rendered string is locale-independent — matters for screenshot diffs. */
function fmtDate(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function EmptyState({
  showGuestCta,
  guestCount,
}: {
  showGuestCta: boolean;
  guestCount: number;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-slate-700 p-10 text-center">
      <div className="text-slate-300 font-medium">
        {showGuestCta ? "No crawls owned by your account yet" : "No crawls yet"}
      </div>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        {showGuestCta ? (
          <>
            The privacy gate filters this view to crawls you own. Crawls
            created before you signed in (or in single-tenant dev mode) live
            in the guest workspace.
          </>
        ) : (
          <>
            Kick off your first crawl — point UAIP at a URL and it&apos;ll map
            the UI, generate stable selectors, and emit ready-to-run test
            code.
          </>
        )}
      </p>

      {/* Guest-workspace CTA — only when authed and the probe found rows
          out there. Click → /?guest=1 → DashboardPage refetches with
          scope=guest and the heading flips to "Guest workspace". */}
      {showGuestCta && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-indigo-700 bg-indigo-500/10 px-3 py-2 text-sm">
          <span className="text-indigo-200">
            {guestCount} crawl{guestCount === 1 ? "" : "s"} in the guest workspace
          </span>
          <Link
            to="/?guest=1"
            className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
          >
            View as guest →
          </Link>
        </div>
      )}

      <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/crawls/new"
          className="inline-flex items-center rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-medium px-4 py-2 text-sm transition"
        >
          New crawl →
        </Link>
        <span className="text-[11px] text-slate-500">
          Self-hosted? You can also run{" "}
          <code className="font-mono text-slate-400">pnpm uaip crawl &lt;url&gt;</code>{" "}
          from the repo root.
        </span>
      </div>
    </div>
  );
}

function LoadingBanner(): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
      Loading crawls…
    </div>
  );
}

function ErrorBanner({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
      <div className="font-medium">Couldn&apos;t reach the API</div>
      <div className="font-mono text-xs mt-1 text-rose-300/80">{message}</div>
      <div className="text-xs mt-2 text-rose-200/80">
        Is the API running on :5174? Try{" "}
        <code className="font-mono">pnpm --filter @uaip/api dev</code>.
      </div>
    </div>
  );
}

// ─────────────────────────── Formatters ───────────────────────────

function isWithinLast24h(iso: string): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 24 * 60 * 60 * 1000;
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m${s}s`;
}
